import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import ModalWindow from '../../common/Modal';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { TEN_MEGABYTES } from '../../../constants/fileSize';
import { alertError, alertSuccess } from '../../../store/actions/alert';
import { uploadFileToPreSignedUrl } from '../../../services/upload';
import {
  updateInventoryQuantitiesViaFileUpload,
  updateVariantBuffersViaFileUpload,
} from '../../../store/actions/api/inventory';
import { UploadFileForm, UploadFileHeader, UploadFileAttachments, UploadFileFooter } from '../upload-file-components';
import { downloadAvailableWarehousesAsCsv } from '../../../utils/downloadAvailableWarehousesAsCsv';
import { DownloadReportIcon } from '../../icons/analytics';
import {
  UPLOAD_BUFFERS_CSV_TEMPLATE_URL,
  UPLOAD_INVENTORY_CSV_TEMPLATE_URL,
} from './constants/upload-inventory-csv-template-url';
import { uploadInventoryFileToastMessages } from './constants/toast-messages';

import { UPLOAD_INVENTORY_FILE_MODAL_TYPES } from '../../../constants/uploadInventoryFileModalTypes';
import styles from './UploadInventoryFileModal.module.scss';
import { CSV_MIME_TYPE } from '../../../utils/csv';
import { useMixpanel } from 'react-mixpanel-browser';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { UPDATE_INVENTORY_VIA_FILE_UPLOAD } from '../../../constants/mixpanel';

export function UploadInventoryFileModal({
  onClose,
  isOpen = false,
  warehouses,
  modalType = UPLOAD_INVENTORY_FILE_MODAL_TYPES.quantity,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);

  const { supplierId } = useParams();
  const mixpanel = useMixpanel();
  const { supplier, user } = useSelector((state) => state.role);

  const dispatch = useDispatch();

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      if (selectedFile.type !== CSV_MIME_TYPE) {
        dispatch(alertError('Please upload a CSV file.'));
        return;
      }

      reportBusinessActionToMixpanel(mixpanel, UPDATE_INVENTORY_VIA_FILE_UPLOAD, {
        supplierName: supplier.name,
        userEmail: user.email,
      });

      setIsSubmitInProgress(true);
      const { path: s3FilePath } = await uploadFileToPreSignedUrl({
        supplierId,
        file: selectedFile,
        resourceType: 'inventory',
      });

      if (modalType === UPLOAD_INVENTORY_FILE_MODAL_TYPES.quantity) {
        await updateInventoryQuantitiesViaFileUpload({ s3FilePath, supplierId });
      }

      if (modalType === UPLOAD_INVENTORY_FILE_MODAL_TYPES.buffers) {
        await updateVariantBuffersViaFileUpload({ s3FilePath, supplierId });
      }

      dispatch(alertSuccess(uploadInventoryFileToastMessages[modalType].success));
      setIsSubmitInProgress(false);
      onClose();
      setSelectedFile(null);
    } catch (error) {
      dispatch(alertError(uploadInventoryFileToastMessages[modalType].error));
      setIsSubmitInProgress(false);
    }
  };

  const handleFileSelect = useCallback(
    (event) => {
      const file = event.target.files[0];

      file && file.size <= TEN_MEGABYTES
        ? setSelectedFile(file)
        : dispatch(alertError('File size is larger than 10 MB. Please upload a smaller file.'));
    },
    [dispatch],
  );

  if (!modalType) return null;

  const uiElements = {
    [UPLOAD_INVENTORY_FILE_MODAL_TYPES.quantity]: {
      title: 'Upload inventory file',
      subtitle: 'Accepted format: CSV, up to 10 MB',
      templateLabel: 'View Inventory CSV Template',
      templateUrl: UPLOAD_INVENTORY_CSV_TEMPLATE_URL,
    },
    [UPLOAD_INVENTORY_FILE_MODAL_TYPES.buffers]: {
      title: 'Upload buffer file',
      subtitle: 'Accepted format: CSV, up to 10 MB',
      templateLabel: 'View Buffer CSV Template',
      templateUrl: UPLOAD_BUFFERS_CSV_TEMPLATE_URL,
    },
  };

  const uploadFileAttachments = [
    modalType === UPLOAD_INVENTORY_FILE_MODAL_TYPES.quantity && (
      <div key="warehouse-ids" onClick={() => downloadAvailableWarehousesAsCsv(warehouses)}>
        <DownloadReportIcon strokeColor="#337ab7" />
        <span>Download Warehouse IDs List</span>
      </div>
    ),
    <a key="inventory-template" href={uiElements[modalType]?.templateUrl} target="_blank" rel="noopener noreferrer">
      <FontAwesomeIcon strokeWidth={0.3} icon={faExternalLinkAlt} />
      <span>{uiElements[modalType]?.templateLabel}</span>
    </a>,
  ].filter(Boolean);

  return (
    <ModalWindow open={isOpen} onClose={onClose} size="medium" center shouldUseModernStyles={true}>
      <div className={styles.modalContent}>
        <UploadFileHeader title={uiElements[modalType].title} subtitle={uiElements[modalType].subtitle} />
        <UploadFileForm selectedFile={selectedFile} handleFileSelect={handleFileSelect} />
        <UploadFileAttachments attachments={uploadFileAttachments} />
        <UploadFileFooter
          handleUpload={handleUpload}
          onClose={onClose}
          disabled={!selectedFile}
          isSubmitInProgress={isSubmitInProgress}
        />
      </div>
    </ModalWindow>
  );
}

UploadInventoryFileModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  modalType: PropTypes.oneOf(Object.values(UPLOAD_INVENTORY_FILE_MODAL_TYPES)),
};
